import React, { FC } from 'react'
import { RouteComponentProps, Route, Switch } from 'react-router-dom'

// Constants
import { RouteParams, routes } from 'constants/routes'

// Utils
import { useURLQuerySearch } from 'utils'
import {
  useForTablet,
  isDraftsRoute,
  isTrashRoute,
} from 'components/Breakpoint'

// Hooks
import useWelcomeModal from 'modules/workspace/hooks/useWelcomeModal'

// Components
import NotFoundView from 'views/NotFoundView'
import WorkspaceSharesList from 'modules/shares/components/WorkspaceSharesList'
import TrashList from 'modules/shares/components/TrashList'
import ShareNavbar from 'modules/shares/components/ShareNavbar'
import ProjectsView from 'modules/projects/views/ProjectsView'
import Banner from 'containers/Banner'
import { PrivateWorkspaceRouteExtraProps } from 'modules/workspace/containers'
import { DynamicLoadingPage } from 'containers/DynamicLoadingPage'
import { BillingStatusBanners } from 'modules/workspace/components'
import { getCanAdministerShares } from 'modules/shares/utils'

// Types
import {
  useGetProjectsQuery,
  useGetWorkspaceSettingsQuery,
  useGetDocumentListSettingsQuery,
} from 'gqlTypes'
import { PersonalWorkspaceBanners } from 'modules/workspace/components/WarningBanners'
import { SearchBar } from 'modules/shares/components/SearchBar'

// Styles
import {
  SearchBarWrapper,
  BannerWrapper,
} from './WorkspaceDocumentsView.styles'
import WorkspaceSidebarLayout from 'modules/workspace/components/WorkspaceSidebarLayout'

/**
 * TYPES
 */
export type WorkspaceDocumentsViewRoutesProps = RouteComponentProps<
  | RouteParams<'WORKSPACE_PROJECT'>
  | RouteParams<'WORKSPACE_SHARES'>
  | RouteParams<'WORKSPACE_TRASH'>
>

export type WorkspaceDocumentsViewProps = PrivateWorkspaceRouteExtraProps &
  WorkspaceDocumentsViewRoutesProps

type ProjectsViewProps = RouteComponentProps<
  Partial<RouteParams<'WORKSPACE_PROJECT'> | RouteParams<'WORKSPACE_DRAFTS'>>
>

type WorkspaceTrashRouteParams = RouteComponentProps<
  Partial<RouteParams<'WORKSPACE_TRASH'>>
>
type WorkspaceSharesRouteParams = RouteComponentProps<
  Partial<RouteParams<'WORKSPACE_SHARES'>>
>

type WorkspaceLibrariesRouteParams = RouteComponentProps<
  Partial<RouteParams<'WORKSPACE_LIBRARIES'>>
>

type WorkspaceSharedWithMeRouteParams = RouteComponentProps<
  Partial<RouteParams<'WORKSPACE_SHARED_WITH_ME'>>
>

/**
 * COMPONENT
 */
const WorkspaceDocumentsView: FC<WorkspaceDocumentsViewProps> = ({
  location,
  history,
  match: {
    params: { workspaceId },
    path,
  },
  workspace,
  workspaces,
}) => {
  useWelcomeModal(workspace)
  const isTabletAndBigger = useForTablet()
  const isSmallerThanTablet = !isTabletAndBigger

  const [search, setSearchValue, debouncedSearch] = useURLQuerySearch(
    location,
    history
  )

  const { data, loading } = useGetProjectsQuery({
    variables: {
      workspaceId,
    },
  })

  const { data: settings } = useGetDocumentListSettingsQuery()
  const { showDocumentsInProjects } = settings || {}

  const workspaceSettings = useGetWorkspaceSettingsQuery({
    variables: { identifier: workspaceId },
    fetchPolicy: 'cache-and-network',
  })

  if (loading && isDraftsRoute(path)) {
    return <DynamicLoadingPage />
  }

  const isUserEditor = workspace.userCanEdit
  const canUserAdministerShares = getCanAdministerShares(workspace)
  const hasDraftsProject = !!data?.workspace.draftsProject.entries[0]

  const isUserAdmin = workspace.userRole === 'ADMIN'
  const { billing } = workspaceSettings.data?.workspace.customer ?? {}
  const { status } = billing ?? {}
  const subscriptionEnd = billing?.subscriptionEnd || undefined
  const sharesCount =
    workspaceSettings.data?.workspace.shares.meta.totalCount ?? 0

  if (isTrashRoute(path) && !canUserAdministerShares) {
    return <NotFoundView />
  }

  if (isDraftsRoute(path) && !hasDraftsProject) {
    // The user is accessing /workspace/${workspaceId}/drafts but there isn't any
    // draft project in this workspace.
    return <NotFoundView />
  }

  return (
    <WorkspaceSidebarLayout
      title="Documents"
      workspaces={workspaces}
      currentWorkspace={workspace}
      navbar={
        <ShareNavbar
          search={search}
          onSearchChange={setSearchValue}
          userRole={workspace.userRole}
        />
      }
      hideFooter>
      <>
        {/* Mount of the banner components */}
        <PersonalWorkspaceBanners
          workspaceId={workspace.identifier}
          workspaceType={workspace.type}
          isOwner={workspace.userIsOwner}
        />

        {status && (
          <BillingStatusBanners
            workspaceId={workspaceId}
            sharesCount={sharesCount}
            billingStatus={status}
            cancelPeriodEnd={subscriptionEnd}
            isUserAdmin={isUserAdmin}
          />
        )}

        {/* Mounting the banner container to render them all */}
        <BannerWrapper $hideMargins="top">
          <Banner />
        </BannerWrapper>

        {isSmallerThanTablet && (
          <SearchBarWrapper>
            <SearchBar
              search={debouncedSearch}
              onSearchChange={setSearchValue}
              userRole={workspace.userRole}
            />
          </SearchBarWrapper>
        )}

        <Switch>
          <Route
            exact
            path={[
              routes.WORKSPACE_PROJECT.template(),
              routes.WORKSPACE_DRAFTS.template(),
            ]}
            render={({ match: { params } }: ProjectsViewProps) => (
              <ProjectsView
                workspaceId={workspaceId}
                projectId={'projectId' in params ? params.projectId : undefined}
                search={search}
                debouncedSearch={debouncedSearch}
                userIsEditor={isUserEditor}
                userCanRenameProject={isUserEditor || canUserAdministerShares}
              />
            )}
          />
          {canUserAdministerShares && (
            <Route
              exact
              path={routes.WORKSPACE_TRASH.template()}
              render={({ match: { params } }: WorkspaceTrashRouteParams) => (
                <TrashList
                  workspaceId={params.workspaceId!}
                  search={debouncedSearch}
                  isUserAdmin={isUserAdmin}
                />
              )}
            />
          )}
          {canUserAdministerShares && (
            <Route
              exact
              path={routes.WORKSPACE_TRASH_PROJECT.template()}
              render={({ match: { params } }: ProjectsViewProps) => (
                <ProjectsView
                  workspaceId={workspaceId}
                  projectId={
                    'projectId' in params ? params.projectId : undefined
                  }
                  search={search}
                  debouncedSearch={debouncedSearch}
                  userIsEditor={isUserEditor}
                  userCanRenameProject={false}
                />
              )}
            />
          )}
          <Route
            exact
            path={routes.WORKSPACE_SHARES.template()}
            render={({ match }: WorkspaceSharesRouteParams) => (
              <WorkspaceSharesList
                workspaceId={match.params.workspaceId!}
                workspace={workspace}
                search={debouncedSearch}
                filter={showDocumentsInProjects ? null : 'NO_PROJECT'}
              />
            )}
          />
          <Route
            exact
            path={routes.WORKSPACE_SHARED_WITH_ME.template()}
            render={({ match }: WorkspaceSharedWithMeRouteParams) => (
              <WorkspaceSharesList
                workspaceId={match.params.workspaceId!}
                search={debouncedSearch}
                filter="SHARED_WITH_ME"
              />
            )}
          />
          <Route
            exact
            path={routes.WORKSPACE_LIBRARIES.template()}
            render={({ match }: WorkspaceLibrariesRouteParams) => (
              <WorkspaceSharesList
                workspaceId={match.params.workspaceId!}
                filter="LIBRARY"
                search={debouncedSearch}
              />
            )}
          />
        </Switch>
      </>
    </WorkspaceSidebarLayout>
  )
}

export default WorkspaceDocumentsView
